const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

const setInert = (element, inert) => {
    if (!element) {
        return;
    }

    element.inert = inert;

    if (inert) {
        element.setAttribute('inert', '');
    } else {
        element.removeAttribute('inert');
    }
};

export const initSiteMegaMenu = (header, { beforeOpen } = {}) => {
    const layer = header.querySelector('[data-mega-layer]');
    const triggers = [...header.querySelectorAll('[data-mega-trigger]')];
    const toggles = [...header.querySelectorAll('[data-mega-toggle]')];
    const panels = [...header.querySelectorAll('[data-mega-panel]')];
    const backdrop = header.querySelector('[data-mega-backdrop]');
    const desktopQuery = window.matchMedia('(min-width: 1101px)');
    const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let activeId = '';
    let openTimer = 0;
    let closeTimer = 0;
    let hideTimer = 0;
    let revealFrame = 0;
    let activateFrame = 0;
    let suppressFocusOpen = false;
    let focusReturn = null;
    let pointerDistance = Infinity;

    const emptyController = {
        open: () => {},
        close: () => {},
        getActiveId: () => '',
    };

    if (!layer || !backdrop || triggers.length === 0 || panels.length === 0) {
        return emptyController;
    }

    const panelFor = (id) => panels.find((panel) => panel.dataset.megaPanel === String(id));
    const triggerFor = (id) => triggers.find((trigger) => trigger.dataset.megaTrigger === String(id));
    const toggleFor = (id) => toggles.find((toggle) => toggle.dataset.megaToggle === String(id));
    const panelFocusables = (panel) => [...panel.querySelectorAll(focusableSelector)]
        .filter((element) => element.getClientRects().length > 0 && !element.closest('[inert]'));
    const setExpanded = (id, expanded) => {
        triggerFor(id)?.setAttribute('aria-expanded', String(expanded));
        toggleFor(id)?.setAttribute('aria-expanded', String(expanded));
    };

    const updateLayerHeight = () => {
        const availableHeight = Math.max(0, window.innerHeight - header.getBoundingClientRect().bottom);
        layer.style.setProperty('--site-mega-layer-height', `${availableHeight}px`);
    };

    const clearTimers = () => {
        window.clearTimeout(openTimer);
        window.clearTimeout(closeTimer);
        window.clearTimeout(hideTimer);
        window.cancelAnimationFrame(revealFrame);
        window.cancelAnimationFrame(activateFrame);
        revealFrame = 0;
        activateFrame = 0;
    };

    const concealPanel = (panel) => {
        panel.classList.remove('is-active');
        panel.setAttribute('aria-hidden', 'true');
        setInert(panel, true);
    };

    const close = ({ restoreFocus = false, immediate = false } = {}) => {
        window.clearTimeout(openTimer);
        if (!activeId) {
            return;
        }

        const closingId = activeId;
        const panel = panelFor(closingId);
        const trigger = triggerFor(closingId);
        activeId = '';
        clearTimers();

        triggers.forEach((candidate) => setExpanded(candidate.dataset.megaTrigger, false));
        header.classList.remove('is-mega-menu-open');
        layer.classList.remove('is-open');
        layer.setAttribute('aria-hidden', 'true');
        setInert(layer, true);

        if (panel) {
            concealPanel(panel);
            hideTimer = window.setTimeout(
                () => {
                    if (panel.dataset.megaPanel !== activeId) {
                        panel.hidden = true;
                    }
                },
                immediate || reducedMotionQuery.matches ? 0 : 420,
            );
        }

        if (restoreFocus) {
            suppressFocusOpen = true;
            (focusReturn || trigger)?.focus({ preventScroll: true });
            window.setTimeout(() => {
                suppressFocusOpen = false;
            }, 0);
        }
    };

    const open = (id, opener) => {
        const normalizedId = String(id || '');
        const panel = panelFor(normalizedId);
        const trigger = triggerFor(normalizedId);

        window.clearTimeout(closeTimer);
        window.clearTimeout(openTimer);

        if (!desktopQuery.matches || !panel || !trigger) {
            return;
        }

        if (opener) {
            focusReturn = opener;
        }

        if (activeId === normalizedId) {
            return;
        }

        clearTimers();
        beforeOpen?.();
        updateLayerHeight();

        panels.forEach((candidate) => {
            if (candidate !== panel) {
                concealPanel(candidate);
                candidate.hidden = true;
            }
        });
        triggers.forEach((candidate) => setExpanded(candidate.dataset.megaTrigger, false));

        activeId = normalizedId;
        pointerDistance = Infinity;
        focusReturn = opener || trigger;
        panel.hidden = false;
        panel.setAttribute('aria-hidden', 'false');
        setInert(panel, false);
        setInert(layer, false);
        layer.setAttribute('aria-hidden', 'false');
        layer.classList.add('is-open');
        header.classList.add('is-mega-menu-open');
        setExpanded(normalizedId, true);

        if (reducedMotionQuery.matches) {
            panel.classList.add('is-active');
            return;
        }

        revealFrame = window.requestAnimationFrame(() => {
            revealFrame = 0;

            if (activeId !== normalizedId) {
                return;
            }

            activateFrame = window.requestAnimationFrame(() => {
                activateFrame = 0;

                if (activeId === normalizedId) {
                    panel.classList.add('is-active');
                }
            });
        });
    };

    const scheduleClose = () => {
        window.clearTimeout(openTimer);
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => {
            if (!panelFor(activeId)?.contains(document.activeElement)) {
                close();
            }
        }, 280);
    };

    triggers.forEach((trigger) => {
        const item = trigger.closest('.SiteHeader__menuItem--mega');
        const id = trigger.dataset.megaTrigger;

        const toggle = toggleFor(id);
        item?.addEventListener('pointerenter', () => {
            if (!hoverQuery.matches) {
                return;
            }

            window.clearTimeout(closeTimer);
            window.clearTimeout(openTimer);
            pointerDistance = Infinity;
            openTimer = window.setTimeout(() => open(id), 160);
        });
        trigger.addEventListener('focus', () => {
            if (!suppressFocusOpen) {
                open(id, trigger);
            }
        });
        trigger.addEventListener('click', () => open(id, trigger));
        item?.addEventListener('pointerleave', scheduleClose);

        toggle?.addEventListener('click', () => {
            if (activeId === id) {
                close();
            } else {
                open(id, toggle);
            }
        });

        const handleControlKeydown = (event) => {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                open(id, event.currentTarget);
                window.requestAnimationFrame(() => {
                    panelFor(id)?.querySelector('.SiteHeader__megaHeading a')?.focus({ preventScroll: true });
                });
            } else if (event.key === 'Escape' && activeId === id) {
                event.preventDefault();
                close({ restoreFocus: true });
            } else if (event.key === 'Tab' && !event.shiftKey && event.currentTarget === toggle && activeId === id) {
                event.preventDefault();
                panelFocusables(panelFor(id))[0]?.focus({ preventScroll: true });
            }
        };
        trigger.addEventListener('keydown', handleControlKeydown);
        toggle?.addEventListener('keydown', handleControlKeydown);
    });

    panels.forEach((panel) => {
        panel.addEventListener('pointerenter', () => {
            pointerDistance = 0;
            window.clearTimeout(closeTimer);
        });
        panel.addEventListener('pointerleave', scheduleClose);
        panel.querySelector('[data-mega-close]')?.addEventListener('click', () => close({ restoreFocus: true }));
        panel.addEventListener('keydown', (event) => {
            if (event.key !== 'Tab' || !activeId) {
                return;
            }

            const focusable = panelFocusables(panel);

            if (event.shiftKey && document.activeElement === focusable[0]) {
                event.preventDefault();
                toggleFor(activeId)?.focus({ preventScroll: true });
            } else if (!event.shiftKey && document.activeElement === focusable.at(-1)) {
                const trigger = triggerFor(activeId);
                const navItems = [...header.querySelectorAll('.SiteHeader__desktopNav > .SiteHeader__menuList > .SiteHeader__menuItem > .SiteHeader__menuItemRow > .SiteHeader__menuLink')];
                const next = navItems[navItems.indexOf(trigger) + 1] || header.querySelector('.SiteHeader__brand');
                event.preventDefault();
                close({ immediate: true });
                next?.focus({ preventScroll: true });
            }
        });
    });
    // A centered panel can be far from its trigger on wide screens. Keep it open
    // while the pointer crosses the backdrop towards it, then use the normal delay.
    layer.addEventListener('pointermove', (event) => {
        if (!activeId || !hoverQuery.matches || event.target !== backdrop) {
            return;
        }

        const bounds = panelFor(activeId).getBoundingClientRect();
        const distance = Math.hypot(
            Math.max(bounds.left - event.clientX, 0, event.clientX - bounds.right),
            Math.max(bounds.top - event.clientY, 0, event.clientY - bounds.bottom),
        );

        if (distance < pointerDistance) {
            scheduleClose();
        }

        pointerDistance = distance;
    });
    backdrop.addEventListener('pointerenter', scheduleClose);
    backdrop.addEventListener('click', () => close({
        restoreFocus: Boolean(panelFor(activeId)?.contains(document.activeElement)),
    }));
    layer.addEventListener('click', (event) => {
        if (event.target instanceof Element && event.target.closest('a[href]')) {
            close();
        }
    });

    header.addEventListener('focusout', () => {
        window.setTimeout(() => {
            const activePanel = panelFor(activeId);
            const control = document.activeElement?.closest('[data-mega-trigger], [data-mega-toggle]');
            if (activeId && !activePanel?.contains(document.activeElement) && !control) {
                close();
            }
        }, 0);
    });

    document.addEventListener('pointerdown', (event) => {
        if (
            activeId
            && event.target instanceof Element
            && !event.target.closest('[data-mega-panel]')
            && !event.target.closest('[data-mega-trigger]')
            && !event.target.closest('[data-mega-toggle]')
            && !event.target.closest('[data-mega-backdrop]')
        ) {
            close();
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && activeId) {
            event.preventDefault();
            close({ restoreFocus: true });
        }
    });

    desktopQuery.addEventListener?.('change', (event) => {
        if (!event.matches) {
            close({ immediate: true });
        }
    });
    window.addEventListener('resize', updateLayerHeight, { passive: true });
    window.addEventListener('orientationchange', updateLayerHeight, { passive: true });

    setInert(layer, true);
    updateLayerHeight();

    return {
        open,
        close,
        getActiveId: () => activeId,
    };
};
